import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { 
  useCreateCollectionHistory, 
  type ContactType, 
  type Outcome,
  type ContactReason 
} from '@/hooks/finance/useCollectionHistory';

const CONTACT_TYPES: { value: ContactType; label: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'call', label: 'Ligação' },
  { value: 'email', label: 'Email' },
  { value: 'in_person', label: 'Presencial' },
];

const CONTACT_REASONS: { value: ContactReason; label: string }[] = [
  { value: 'collection', label: 'Cobrança de parcela' },
  { value: 'follow_up', label: 'Retorno de contato' },
  { value: 'promise_check', label: 'Verificar promessa' },
  { value: 'renegotiation', label: 'Renegociação' },
  { value: 'confirmation', label: 'Confirmar pagamento' },
];

const OUTCOMES: { value: Outcome; label: string }[] = [
  { value: 'no_answer', label: 'Não atendeu' },
  { value: 'promised', label: 'Prometeu pagar' },
  { value: 'refused', label: 'Recusou' },
  { value: 'negotiating', label: 'Negociando' },
  { value: 'partial_payment', label: 'Pagamento parcial' },
  { value: 'paid', label: 'Pagou' },
  { value: 'renegotiated', label: 'Contrato renegociado' },
  { value: 'wrong_number', label: 'Número errado' },
  { value: 'callback_requested', label: 'Pediu retorno' },
];

const formSchema = z.object({
  reason: z.enum(['collection', 'follow_up', 'promise_check', 'renegotiation', 'confirmation']),
  contact_type: z.enum(['whatsapp', 'call', 'email', 'in_person']),
  outcome: z.enum(['no_answer', 'promised', 'refused', 'negotiating', 'partial_payment', 'paid', 'renegotiated', 'wrong_number', 'callback_requested']),
  notes: z.string().optional(),
  promised_date: z.string().optional(),
  promised_amount: z.coerce.number().positive().optional().or(z.literal('')),
  callback_date: z.string().optional(),
  next_action: z.string().optional(),
}).refine((data) => {
  if (data.outcome === 'promised' && !data.promised_date) {
    return false;
  }
  return true;
}, {
  message: 'Data prometida é obrigatória quando o resultado é "Prometeu pagar"',
  path: ['promised_date'],
}).refine((data) => {
  if (data.outcome === 'callback_requested' && !data.callback_date) {
    return false;
  }
  return true;
}, {
  message: 'Data de retorno é obrigatória quando o resultado é "Pediu retorno"',
  path: ['callback_date'],
});

type FormData = z.infer<typeof formSchema>;

interface RegisterContactModalProps {
  open: boolean;
  onClose: () => void;
  paymentId: string;
  contactId: string;
  clientName: string;
  prefill?: Partial<Pick<FormData, 'reason' | 'contact_type' | 'outcome'>>;
  onSaved?: () => void;
}

export function RegisterContactModal({
  open,
  onClose,
  paymentId,
  contactId,
  clientName,
  prefill,
  onSaved,
}: RegisterContactModalProps) {
  const createMutation = useCreateCollectionHistory();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: 'collection',
      contact_type: 'call',
      outcome: 'no_answer',
      notes: '',
      promised_date: '',
      promised_amount: '',
      callback_date: '',
      next_action: '',
    },
  });

  const resetWithPrefill = () => {
    form.reset({
      reason: prefill?.reason ?? 'collection',
      contact_type: prefill?.contact_type ?? 'call',
      outcome: prefill?.outcome ?? 'no_answer',
      notes: '',
      promised_date: '',
      promised_amount: '',
      callback_date: '',
      next_action: '',
    });
  };

  // Ensure every open starts with a fresh form, optionally prefilled for post-call logging.
  useEffect(() => {
    if (open) resetWithPrefill();
  }, [open, prefill?.reason, prefill?.contact_type, prefill?.outcome, paymentId, contactId]);

  const outcome = form.watch('outcome');
  const showPromiseFields = outcome === 'promised';
  const showCallbackField = outcome === 'callback_requested';

  const onSubmit = async (data: FormData) => {
    await createMutation.mutateAsync({
      payment_id: paymentId,
      contact_id: contactId,
      reason: data.reason,
      contact_type: data.contact_type,
      outcome: data.outcome,
      notes: data.notes || undefined,
      promised_date: data.promised_date || undefined,
      promised_amount: data.promised_amount ? Number(data.promised_amount) : undefined,
      callback_date: data.callback_date || undefined,
      next_action: data.next_action || undefined,
    });

    resetWithPrefill();
    onSaved?.();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Registrar Contato de Cobrança</DialogTitle>
        </DialogHeader>

        <div className="mb-4 p-3 bg-muted rounded-lg">
          <p className="text-sm font-medium">{clientName}</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motivo do Contato</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONTACT_REASONS.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="contact_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Contato</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CONTACT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="outcome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Resultado</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {OUTCOMES.map((outcome) => (
                          <SelectItem key={outcome.value} value={outcome.value}>
                            {outcome.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {showPromiseFields && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <FormField
                  control={form.control}
                  name="promised_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data Prometida</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="promised_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor Prometido</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          step="0.01" 
                          placeholder="Opcional"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {showCallbackField && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <FormField
                  control={form.control}
                  name="callback_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data para Retorno</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="next_action"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Próxima Ação</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Ex: Ligar novamente amanhã às 14h"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Descreva os detalhes do contato..."
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
